import React, { FunctionComponent, useEffect, useState } from 'react';
import { Button, Chip, IconButton } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import ConfirmDialog from 'src/components/ConfirmDialog';
import AddClaudeCredentialModal, {
    IClaudeCredentialValues,
} from 'src/components/AddClaudeCredentialModal';
import {
    IClaudeValidateRequest,
    IDatadogTestConnectionResult,
    IWorkspaceSetting,
} from 'src/hooks/useWorkspaceSettings';

export interface IClaudeCredentialsSectionProps {
    workspaceId: string
    settingDefinitions: IWorkspaceSetting[]
    settingValues: { [name: string]: string | null }
    setSettingValues: React.Dispatch<React.SetStateAction<{ [name: string]: string | null }>>
    validateClaudeCredential: (workspaceId: string, body: IClaudeValidateRequest) => Promise<IDatadogTestConnectionResult>
    deleteWorkspaceSetting: (workspaceId: string, name: string) => Promise<void>
    saveSuccessSignal?: string | null
}

const CLAUDE_API_KEY = 'claude.api_key'

//
// "Claude" integration section (MEL-574), rendered under Settings → Integrations
// beside Datadog. Clones the Datadog credentials section: masked "Connected" chip,
// Update/Add + disconnect, staging the key into the workspace-form settingValues map
// (persisted, encrypted and write-only, on the workspace PUT).
//
const ClaudeCredentialsSection: FunctionComponent<IClaudeCredentialsSectionProps> = ({
    workspaceId,
    settingDefinitions,
    settingValues,
    setSettingValues,
    validateClaudeCredential,
    deleteWorkspaceSetting,
    saveSuccessSignal,
}) => {
    const apiKeyDef = settingDefinitions.find(s => s.name === CLAUDE_API_KEY)

    // settingValues[CLAUDE_API_KEY] tri-state: undefined = defer to server,
    // null = staged removal, string = staged save.
    const staged = settingValues[CLAUDE_API_KEY]
    const isConfigured = staged === undefined ? (apiKeyDef?.configured ?? false) : staged !== null
    const valueHint = apiKeyDef?.valueHint

    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isDisconnectOpen, setIsDisconnectOpen] = useState(false)
    const [showStagedBanner, setShowStagedBanner] = useState(false)
    const [removeError, setRemoveError] = useState<string | null>(null)

    useEffect(() => {
        if (saveSuccessSignal) setShowStagedBanner(false)
    }, [saveSuccessSignal])

    // validated against Anthropic before the key is staged, so an unusable key is never saved
    const handleValidate = (values: IClaudeCredentialValues) =>
        validateClaudeCredential(workspaceId, { apiKey: values.apiKey })

    const handleSaved = (values: IClaudeCredentialValues) => {
        setSettingValues(prev => ({ ...prev, [CLAUDE_API_KEY]: values.apiKey }))
        setShowStagedBanner(true)
    }

    // Removal is persisted immediately (DELETE /settings/{name}), not staged for the
    // workspace-form Save — the user expects "Remove" to take effect on click. Only after
    // the server confirms do we clear the local staged value so the card flips to empty.
    const handleDisconnectConfirm = async () => {
        setRemoveError(null)
        try {
            await deleteWorkspaceSetting(workspaceId, CLAUDE_API_KEY)
            // server confirmed the removal — flip the card to empty immediately (ConfirmDialog
            // closes itself once this resolves). We never rethrow: a rejection would leave the
            // dialog's confirm button stuck in its processing state.
            setSettingValues(prev => ({ ...prev, [CLAUDE_API_KEY]: null }))
            setShowStagedBanner(false)
        } catch (e) {
            setRemoveError('Could not remove the Claude API key. Please try again.')
        }
    }

    // No definition (backend doesn't expose the claude setting yet): render nothing.
    if (!apiKeyDef) return null

    return (
        <div
            data-testid="claude-credentials-section"
            style={{ margin: '25px auto', width: '100%', padding: '0 5px 0 0' }}>
            <div>
                <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* Anthropic "sunburst" mark on the brand clay colour — an inline SVG (the app
                        has no official Anthropic logo asset). Swap for the official SVG if provided. */}
                    <svg
                        data-testid="claude-brand-icon"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        style={{ flexShrink: 0 }}>
                        <rect width="24" height="24" rx="5" fill="#D97757" />
                        <g stroke="#fff" strokeWidth="1.5" strokeLinecap="round">
                            {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map(angle => (
                                <line key={angle} x1="12" y1="4.5" x2="12" y2="8.4"
                                    transform={`rotate(${angle} 12 12)`} />
                            ))}
                        </g>
                    </svg>
                    Claude
                </h3>
                {!isConfigured && (
                    <div
                        data-testid="claude-empty-card"
                        style={{
                            padding: 16,
                            backgroundColor: '#fafafa',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 16,
                            width: '100%',
                        }}>
                        <p style={{ margin: 0, maxWidth: 360 }}>
                            There&apos;s no credential present for Claude yet. Add a credential to use Melty AI.
                        </p>
                        <Button
                            data-testid="claude-add-credential"
                            variant="outlined"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() => setIsModalOpen(true)}>
                            Add credential
                        </Button>
                    </div>
                )}
                {isConfigured && (
                    <div data-testid="claude-configured-card" style={{ width: '100%' }}>
                        <div
                            style={{
                                padding: 16,
                                border: '1px solid #ddd',
                                borderRadius: 4,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 8,
                            }}>
                            <span data-testid="claude-masked-value" style={{ fontFamily: 'monospace', letterSpacing: 1 }}>
                                {valueHint ? `...${valueHint}` : 'Credential set'}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Chip
                                    data-testid="claude-credential-chip"
                                    size="small"
                                    label="Active"
                                    style={{ backgroundColor: '#4caf50', color: '#fff' }} />
                                <IconButton
                                    data-testid="claude-disconnect"
                                    size="small"
                                    aria-label="Remove Claude API key"
                                    onClick={() => { setRemoveError(null); setIsDisconnectOpen(true) }}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </div>
                        </div>
                        {showStagedBanner && (
                            <Alert data-testid="claude-staged-banner" severity="success" style={{ marginTop: 12 }}>
                                Credential is tested successfully. Click the &apos;Save&apos; button to save the credentials.
                            </Alert>
                        )}
                        {removeError && (
                            <Alert data-testid="claude-remove-error" severity="error" style={{ marginTop: 12 }}>
                                {removeError}
                            </Alert>
                        )}
                    </div>
                )}
            </div>
            <AddClaudeCredentialModal
                isOpen={isModalOpen}
                apiKeyDefinition={apiKeyDef}
                handleClose={() => setIsModalOpen(false)}
                handleValidate={handleValidate}
                handleSaved={handleSaved}
            />
            <ConfirmDialog
                isOpen={isDisconnectOpen}
                title="Remove Claude API key?"
                message="AI pipeline diagnostics will stop working until a new key is added."
                cancelLabel="Cancel"
                confirmLabel="Remove"
                handleClose={() => setIsDisconnectOpen(false)}
                handleConfirm={handleDisconnectConfirm}
            />
        </div>
    )
}

export default ClaudeCredentialsSection
